import { useCallback, useMemo, useState } from "react";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  GridColDef,
  GridRenderCellParams,
  DataGridPro,
  GridRowOrderChangeParams,
} from "@mui/x-data-grid-pro";
import { useAtom } from "jotai";
import { FixedPanel } from "../../../components/FixedPanel";
import { SelectFromList } from "../../../components/SelectFromList";
import { IconTypes } from "../../../components/icons.types";
import {
  LinkPrefix,
  ProductListExtOptions,
  storeLinkField,
} from "../../../features/catalogs/product-list.ext";
import {
  DataType,
  DataTypeName,
  ExtendedDataType,
  moveEntry,
} from "../../../features/data/data-types";
import {
  VariantPrefix,
  ImageFieldParams,
  parseImageField,
  imagePositions,
  ImagePosition,
  TextFieldParams,
  parseTextField,
  formatTextField,
} from "../../../features/design/design.types";
import { productDbAtom } from "../../../features/products/product.state";
import { Transition } from "../../../components/Transitions";

type FieldRow = { name: string; dataType?: DataType };

const presetFields: FieldRow[] = [
  { name: "Name" },
  { name: "Code" },
  { name: "Price", dataType: DataTypeName.Money },
  { name: "Description" },
  { name: ExtendedDataType.Category },
  { name: "Image", dataType: DataTypeName.Image },
  { name: "Product Page Link" },
  { name: "Product Page Link:Name" },
  { name: "Product Page Link:Code" },
  { name: "Product Order Link" },
  { name: "Quantity:Empty" },
];

const variantFields: FieldRow[] = [
  { name: "Option" },
  { name: "Price", dataType: DataTypeName.Money },
  { name: "SKU" },
  { name: "Image", dataType: DataTypeName.Image },
  { name: "Description" },
  { name: "Quantity", dataType: DataTypeName.Number },
  { name: "Barcode" },
  { name: "BarcodeImage" },
  { name: "BarcodeImageExt" },
].map((f) => ({ name: VariantPrefix + ":" + f.name, dataType: f.dataType }));

const dataTypeCell: GridColDef = {
  field: "type",
  headerName: "",
  width: 25,
  disableColumnMenu: true,
  sortable: false,
  renderCell: (params: GridRenderCellParams) => {
    return (
      <Box className="data-type-col" title={params.row.dataType}>
        {params.row.dataType === DataTypeName.Image && <IconTypes.Image />}
        {params.row.dataType === DataTypeName.Money && <IconTypes.Money />}
        {params.row.dataType === DataTypeName.Number && <IconTypes.Number />}
        {params.row.dataType === DataTypeName.RichText && <IconTypes.Text />}
        {params.row.dataType === DataTypeName.Date && <IconTypes.Date />}
      </Box>
    );
  },
};

export const ProductListExtFields = ({
  extension,
  onChange,
  categoryId,
}: ProductListExtOptions & { categoryId: string }) => {
  const [productDb] = useAtom(productDbAtom);
  const [editField, setEditField] = useState<ImageFieldParams>();
  const [editTextField, setEditTextField] =
    useState<TextFieldParams & { dataType?: DataType }>();

  // Formatted text cannot be shortened AND stay formatted — the server counts
  // characters on the raw string, so a length always strips. Only offer the
  // length once the field is plain; on a Text field there is nothing to strip,
  // so it is always available.
  const canSetLength =
    !!editTextField &&
    (editTextField.plain || editTextField.dataType !== DataTypeName.RichText);

  const getDataType = useCallback(
    (name: string) => {
      if (name === DataTypeName.Image) return DataTypeName.Image;
      if (name.startsWith(VariantPrefix + ":"))
        return variantFields.find((f) => f.name === name)?.dataType;
      const c = Array.from(
        productDb.categoryMap.get(categoryId).fieldMap.values()
      ).find((f) => f.name === name);
      return c?.dataType;
    },
    [categoryId, productDb.categoryMap]
  );

  const categoryFieldColumns: GridColDef[] = useMemo(
    () => [
      {
        field: "add",
        headerName: "",
        width: 50,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box>
              <IconButton
                className="action"
                size="small"
                onClick={() => {
                  let name = params.row.name as string;
                  if (params.row.dataType === DataTypeName.Image)
                    name += ":Fit:40:40";
                  onChange?.({
                    ...extension,
                    fields: [...extension.fields, name],
                  });
                }}
              >
                <IconTypes.AddAction />
              </IconButton>
            </Box>
          );
        },
      },
      dataTypeCell,
      {
        field: "name",
        headerName: "Add category field to the list",
        flex: 1,
      },
    ],
    [extension, onChange]
  );

  const extFieldColumns: GridColDef[] = useMemo(
    () => [
      {
        field: "remove",
        headerName: "",
        width: 50,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          return (
            <Box>
              <IconButton
                className="action"
                size="small"
                onClick={() =>
                  onChange?.({
                    ...extension,
                    fields: extension.fields.filter(
                      (f) => f !== params.row.name
                    ),
                  })
                }
              >
                <IconTypes.RemoveAction />
              </IconButton>
            </Box>
          );
        },
      },
      dataTypeCell,
      {
        field: "name",
        headerName: "Product List fields",
        flex: 1,
      },
      {
        field: "edit",
        headerName: "",
        width: 50,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const { args, textArgs } = params.row;
          if (!args && !textArgs) return null;
          return (
            <Box>
              <IconButton
                className="action"
                size="small"
                onClick={() => {
                  if (args) {
                    setEditField({ ...args, field: params.row.name });
                    return;
                  }
                  setEditTextField({
                    // Re-parse with the data type so a legacy bare length on a
                    // Rich Text field shows as what it already is: plain.
                    ...parseTextField(params.row.name, params.row.dataType),
                    field: params.row.name,
                    dataType: params.row.dataType,
                  });
                }}
              >
                <IconTypes.Settings />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [extension, onChange]
  );

  const extRows = useMemo(() => {
    if (!extension.fields) return [];

    return extension.fields.map((f) => {
      const args = parseImageField(f);
      const dataType = getDataType(args.name);
      const isText =
        dataType === DataTypeName.Text || dataType === DataTypeName.RichText;
      return {
        id: f,
        name: f,
        args: dataType === DataTypeName.Image ? args : null,
        textArgs: isText ? parseTextField(f) : null,
        dataType,
      };
    });
  }, [extension.fields, getDataType]);

  const fieldRows = useMemo(() => {
    const category = productDb.categoryMap.get(categoryId);
    const categoryFields = category.group.fields.map((f) => ({
      name: f.name,
      dataType: f.dataType,
    }));

    const optionFields = category.options
      .map((o) => [
        VariantPrefix + ":" + o.name,
        VariantPrefix + ":" + o.name + ":Unique",
      ])
      .flatMap((f) => f);

    // The product name linked to its store page, when the category has one (Shopify imports)
    const linkFields: FieldRow[] = categoryFields.some(
      (f) => f.name === storeLinkField
    )
      ? [{ name: `${LinkPrefix}:${storeLinkField}:Name` }]
      : [];

    const fields: FieldRow[] = [
      ...presetFields,
      ...linkFields,
      ...categoryFields,
      ...optionFields.map((f) => ({ name: f })),
      ...variantFields,
    ].filter(
      (f) =>
        !extRows.some(
          (e) =>
            e.name === f.name ||
            e.args?.name === f.name ||
            e.textArgs?.name === f.name
        )
    );

    const uniqueNames = new Set();

    const uniqueFields = fields.filter((f) => {
      if (uniqueNames.has(f.name)) {
        return false;
      }
      uniqueNames.add(f.name);
      return true;
    });

    return uniqueFields.map((f) => ({
      id: f.name,
      name: f.name,
      dataType: f.dataType,
    }));
  }, [categoryId, extRows, productDb.categoryMap]);

  const handleOrderChange = useCallback(
    (params: GridRowOrderChangeParams) => {
      const fields = moveEntry(
        extension.fields,
        params.oldIndex,
        params.targetIndex
      );
      onChange?.({ ...extension, fields });
    },
    [extension, onChange]
  );

  return (
    <Box
      sx={{
        "& .data-type-col": {
          display: "flex",
          alignItems: "center",
          height: "100%",
          "& svg": { width: 16, height: 16 },
        },
      }}
    >
      <FixedPanel overflow="auto" container="#plist-container" breakpoint="lg">
        <Box sx={{ height: "100%", display: "flex", gap: 2, p: 2 }}>
          <Box
            sx={{
              height: "100%",
              minHeight: 100,
              minWidth: 250,
              display: "flex",
              flex: 1,
            }}
          >
            <DataGridPro
              rows={extRows}
              columns={extFieldColumns}
              hideFooter
              rowReordering
              onRowOrderChange={handleOrderChange}
            />
          </Box>
          <Box
            sx={{
              height: "100%",
              minHeight: 100,
              minWidth: 250,
              display: "flex",
              flex: 1,
            }}
          >
            <DataGridPro
              rows={fieldRows}
              columns={categoryFieldColumns}
              hideFooter
            />
          </Box>
        </Box>
      </FixedPanel>
      <Dialog
        open={!!editField}
        onClose={() => setEditField(null)}
        TransitionComponent={Transition}
      >
        {editField && (
          <>
            <DialogTitle>Edit {editField.name} field</DialogTitle>
            <DialogContent>
              <Box sx={{ py: 2, display: "flex", gap: 1 }}>
                <SelectFromList
                  entries={imagePositions.slice() as string[]}
                  value={editField.pos}
                  setValue={(v) =>
                    setEditField({ ...editField, pos: v as ImagePosition })
                  }
                  label="Position"
                  sx={{ minWidth: 150 }}
                />
                <TextField
                  label="Width"
                  sx={{ width: 70 }}
                  value={editField.w}
                  onChange={(e) =>
                    setEditField({ ...editField, w: +e.target.value })
                  }
                  type="number"
                />
                <TextField
                  label="Height"
                  sx={{ width: 70 }}
                  value={editField.h}
                  onChange={(e) =>
                    setEditField({ ...editField, h: +e.target.value })
                  }
                  type="number"
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditField(null)}>Cancel</Button>
              <Button
                onClick={() => {
                  const fields = extension.fields.map((f) => {
                    if (f === editField.field) {
                      return `${editField.name}:${editField.pos}:${editField.w}:${editField.h}`;
                    }
                    return f;
                  });
                  onChange?.({ ...extension, fields });
                  setEditField(null);
                }}
                variant="contained"
                color="primary"
              >
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
      <Dialog
        open={!!editTextField}
        onClose={() => setEditTextField(null)}
        TransitionComponent={Transition}
      >
        {editTextField && (
          <>
            <DialogTitle>Edit {editTextField.name} field</DialogTitle>
            <DialogContent>
              <Box
                sx={{
                  py: 2,
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  minHeight: 90,
                }}
              >
                <FormControlLabel
                  label="Plain text"
                  title="Drop the field's own formatting and show its text only"
                  control={
                    <Switch
                      checked={editTextField.plain}
                      onChange={(e) =>
                        setEditTextField({
                          ...editTextField,
                          plain: e.target.checked,
                          // The length control goes away with it, so don't leave
                          // a value behind that the user can no longer see.
                          len: e.target.checked ? editTextField.len : 0,
                        })
                      }
                    />
                  }
                />
                {canSetLength ? (
                  <TextField
                    label="Max length"
                    title="Cut the text at the first word past this many characters"
                    sx={{ width: 150 }}
                    value={editTextField.len || ""}
                    onChange={(e) =>
                      setEditTextField({
                        ...editTextField,
                        len: +e.target.value,
                      })
                    }
                    type="number"
                    InputProps={{
                      endAdornment: editTextField.len ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            title="No limit"
                            onClick={() =>
                              setEditTextField({ ...editTextField, len: 0 })
                            }
                          >
                            <IconTypes.Clear fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                ) : (
                  <Typography variant="caption" sx={{ maxWidth: 230 }}>
                    Switch on plain text to set a maximum length. Shortening
                    formatted text always drops its formatting.
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEditTextField(null)}>Cancel</Button>
              <Button
                onClick={() => {
                  const fields = extension.fields.map((f) =>
                    f === editTextField.field
                      ? formatTextField(editTextField)
                      : f
                  );
                  onChange?.({ ...extension, fields });
                  setEditTextField(null);
                }}
                variant="contained"
                color="primary"
              >
                Save
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};
